// src/app/features/otp/otpSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import otpService from "./otpService";

/**
 * ✅ PHONE-ONLY OTP SLICE
 * - Forces type="phone" in both send + verify
 * - Stores normalized identity (if backend returns it)
 * - Keeps purpose (login/signup) so UI can use it
 */

// ✅ Send OTP (PHONE ONLY)
export const sendOtp = createAsyncThunk(
  "otp/send",
  async ({ identity, purpose = "login" }, thunkAPI) => {
    try {
      return await otpService.sendOtp({
        identity,
        type: "phone",
        purpose,
      });
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to send OTP";
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

// ✅ Verify OTP (PHONE ONLY)
export const verifyOtp = createAsyncThunk(
  "otp/verify",
  async ({ identity, code, purpose = "login" }, thunkAPI) => {
    try {
      return await otpService.verifyOtp({
        identity,
        code,
        type: "phone",
        purpose,
      });
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to verify OTP";
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

const initialState = {
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: "",

  // helpful state for UI
  identity: "", // the phone number user typed
  normalized: "", // backend-normalized phone (if returned)
  purpose: "login", // last used purpose

  // last responses
  sendRes: null,
  verifyRes: null,
};

const otpSlice = createSlice({
  name: "otp",
  initialState,
  reducers: {
    resetOtpState: () => initialState,

    clearOtpMessage: (state) => {
      state.message = "";
      state.isError = false;
      state.isSuccess = false;
    },

    // optional: keep identity in redux (nice for multi-step forms)
    setOtpIdentity: (state, action) => {
      state.identity = action.payload || "";
    },

    // optional: clear responses but keep identity/purpose
    clearOtpResponses: (state) => {
      state.sendRes = null;
      state.verifyRes = null;
      state.isError = false;
      state.isSuccess = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== SEND OTP =====
      .addCase(sendOtp.pending, (state, action) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";

        state.identity = action.meta?.arg?.identity || state.identity;
        state.purpose = action.meta?.arg?.purpose || state.purpose;
      })
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.sendRes = action.payload;
        state.message = action.payload?.message || "OTP sent";

        // if backend returns normalized phone, store it
        state.normalized = action.payload?.normalized || state.normalized;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || "Failed to send OTP";
      })

      // ===== VERIFY OTP =====
      .addCase(verifyOtp.pending, (state, action) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";

        state.identity = action.meta?.arg?.identity || state.identity;
        state.purpose = action.meta?.arg?.purpose || state.purpose;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.verifyRes = action.payload;
        state.message = action.payload?.message || "OTP verified";

        state.normalized = action.payload?.normalized || state.normalized;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload || "Failed to verify OTP";
      });
  },
});

export const {
  resetOtpState,
  clearOtpMessage,
  setOtpIdentity,
  clearOtpResponses,
} = otpSlice.actions;

export default otpSlice.reducer;
